import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, makeStyles, IconButton } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { HexGrid, Layout, Hexagon, Text } from 'react-hexgrid';
import { withRouter } from 'react-router-dom';
import { useLastStatusObjectiveByNode } from '../../../hooks/useLastStatusObjectiveByNode';
import { AtCircularProgress } from '../../../components/AtCircularProgress';
import codes_ok from '../../../data/codes_ok';

const STATUS_COLORS = {
  Uptime: '#10B981',
  uptime: '#10B981',
  ok: '#10B981',
  OK: '#10B981',
  Partial: '#F59E0B',
  Parcial: '#F59E0B',
  parcial: '#F59E0B',
  partial: '#F59E0B',
  Downtime: '#EF4444',
  downtime: '#EF4444',
  maintenance: '#6366F1',
  mantenimiento: '#6366F1',
};

const useStyles = makeStyles((theme) => ({
  content: {
    flex: 1,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
  },
  hex: {
    transition: 'fill 0.2s ease',
    '&:hover': {
      filter: 'brightness(0.85)',
    },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: theme.spacing(1),
  },
  emptyIcon: {
    fontSize: 48,
    opacity: 0.3,
    color: '#94A3B8',
  },
  emptyText: {
    fontSize: 14,
    color: '#94A3B8',
    fontWeight: 500,
  },
  gridWrapper: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'opacity 0.35s ease, transform 0.35s ease',
  },
  entering: {
    opacity: 1,
    transform: 'scale(1)',
  },
  exiting: {
    opacity: 0,
    transform: 'scale(0.85)',
  },
  backButton: {
    position: 'absolute',
    top: 4,
    left: 4,
    zIndex: 2,
    backgroundColor: 'rgba(255,255,255,0.85)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,1)',
    },
  },
  breadcrumb: {
    position: 'absolute',
    top: 10,
    left: 44,
    zIndex: 2,
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#475569',
    maxWidth: 'calc(100% - 60px)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

function computeHexLayout(count) {
  const cols = Math.ceil(Math.sqrt(Math.max(count, 1) * 1.3));
  const rows = Math.ceil(Math.max(count, 1) / cols);
  const hexSize = 10;
  const hexWidth = Math.sqrt(3) * hexSize;
  const hexHeight = 2 * hexSize;
  const totalWidth = (cols + 0.5) * hexWidth * 1.1;
  const totalHeight = (rows * 0.75 + 0.25) * hexHeight * 1.1;
  const margin = 15;
  const vbW = totalWidth + margin;
  const vbH = totalHeight + margin;

  return {
    columns: cols,
    layoutSize: { x: hexSize, y: hexSize },
    viewBox: `${-vbW / 2} ${-vbH / 2} ${vbW} ${vbH}`,
  };
}

const HexGridView = React.memo(({ items, columns, layoutSize, viewBox, onClickItem, onHoverItem, onLeaveItem, classes }) => (
  <HexGrid
    width="100%"
    height="100%"
    viewBox={viewBox}
    preserveAspectRatio="xMidYMid meet"
  >
    <Layout flat={false} spacing={1.1} size={layoutSize} origin={{ x: 0, y: 0 }}>
      {items.map((item, index) => {
        const r = Math.floor(index / columns);
        const q = (index % columns) - Math.floor(r / 2);
        const rowsCount = Math.ceil(items.length / columns);
        const offsetQ = (columns - 1) / 2 - Math.floor((rowsCount - 1) / 2) / 2;
        const offsetR = (rowsCount - 1) / 2;

        return (
          <Hexagon
            key={item.id || index}
            q={q - offsetQ}
            r={r - offsetR}
            s={-(q - offsetQ) - (r - offsetR)}
            cornerRadius={4}
            className={classes.hex}
            style={{ fill: item.color || '#CBD5E1', cursor: onClickItem ? 'pointer' : 'default' }}
            onClick={() => {
              onLeaveItem && onLeaveItem()
              onClickItem && onClickItem(item);
            }}
            onMouseEnter={(e) => onHoverItem && onHoverItem(e, item)}
            onMouseLeave={() => onLeaveItem && onLeaveItem()}
            onMouseMove={(e) => onHoverItem && onHoverItem(e, item)}
          >
            <Text
              style={{
                fill: '#fff',
                fontSize: 2,
                fontWeight: 600,
                pointerEvents: 'none',
              }}
            >
              {item.label.length > 15 ? `${item.label.slice(0, 12)}..` : item.label}
            </Text>
          </Hexagon>
        );
      })}
    </Layout>
  </HexGrid>
));

const HoneycombPanelBase = ({ data, history, loading }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [selectedObjective, setSelectedObjective] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [animPhase, setAnimPhase] = useState('idle');
  const [tooltipInfo, setTooltipInfo] = useState({ visible: false, x: 0, y: 0, text: '' });
  // Tracks corrected statuses discovered during drill-down
  const [statusOverrides, setStatusOverrides] = useState({});

  const handleHover = useCallback((e, item) => {
    if (e && e.clientX !== undefined) {
      setTooltipInfo({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        text: item.tooltip
      });
    }
  }, []);

  const handleLeave = useCallback(() => {
    setTooltipInfo(prev => ({ visible: false }));
  }, []);

  const { data: nodeStatusData, isLoading: isLoadingNodes } = useLastStatusObjectiveByNode(
    selectedObjective?.objetivo_id
  );

  const okCodesSet = useMemo(() => new Set(codes_ok.map(c => c.id)), []);

  // Reset overrides when backend data refreshes
  React.useEffect(() => {
    setStatusOverrides({});
  }, [data]);

  // When nodes load for a drilled-down objective, check for inconsistency with parent state
  React.useEffect(() => {
    if (!selectedObjective || !nodeStatusData?.nodos || nodeStatusData.nodos.length === 0) return;


    const objId = selectedObjective.objetivo_id;
    const MONITORED_STATES = new Set(['uptime', 'ok', 'downtime', 'parcial', 'partial']);

    const monitoredNodes = nodeStatusData.nodos.filter(n => 
      MONITORED_STATES.has((n.status || '').toLowerCase())
    );

    if (monitoredNodes.length === 0) return;

    let hasUptime = false;
    let hasDowntime = false;
    let hasPartial = false;

    for (const nodo of monitoredNodes) {
      const st = (nodo.status || '').toLowerCase();
      if (st === 'uptime' || st === 'ok') hasUptime = true;
      else if (st === 'downtime') hasDowntime = true;
      else if (st === 'parcial' || st === 'partial') hasPartial = true;
    }

    let derivedStatus;
    if (hasPartial || (hasUptime && hasDowntime)) {
      derivedStatus = 'Partial';
    } else if (hasDowntime && !hasUptime) {
      derivedStatus = 'Downtime';
    } else {
      derivedStatus = 'Uptime';
    }

    // Find the original status from backend data
    const originalStatus = ['Uptime', 'Partial', 'Downtime'].find(st =>
      (data?.[st] || []).some(obj => obj.objetivo_id === objId)
    ) || 'Uptime';

    // Only override if there's an actual inconsistency
    if (derivedStatus !== originalStatus) {
      setStatusOverrides(prev => {
        if (prev[objId] === derivedStatus) return prev;
        return { ...prev, [objId]: derivedStatus };
      });
    }
  }, [selectedObjective, nodeStatusData, data]);

  const objectiveItems = useMemo(() => {
    const states = ['Uptime', 'Partial', 'Downtime'];
    return states.flatMap((status) =>
      (data?.[status] || []).map((item) => {
        const correctedStatus = statusOverrides[item.objetivo_id] || status;
        return {
          id: `obj-${item.objetivo_id}`,
          label: item.nombre,
          color: STATUS_COLORS[correctedStatus] || '#CBD5E1',
          tooltip: `${item.nombre} - ${correctedStatus}`,
          raw: item,
          status: correctedStatus,
        };
      })
    );
  }, [data, statusOverrides]);

  const nodeItems = useMemo(() => {
    if (!nodeStatusData?.nodos) return [];
    return nodeStatusData.nodos.map((nodo) => ({
      id: `nodo-${nodo.nodo_id}`,
      label: nodo.titulo || nodo.nombre || `Nodo ${nodo.nodo_id}`,
      color: STATUS_COLORS[nodo.status] || '#CBD5E1',
      tooltip: `${nodo.titulo || nodo.nombre} - ${nodo.status}`,
      raw: nodo,
    }));
  }, [nodeStatusData]);

  const stepItems = useMemo(() => {
    if (!selectedNode?.pasos) return [];
    
    return selectedNode.pasos.map((paso, pasoIdx) => {
      const stepName = paso.nombre_paso || `Paso ${pasoIdx + 1}`;
      const errors = (paso.codigo_descripcion || []).filter(err => !okCodesSet.has(err.codigo_id));
      const isOk = errors.length === 0;
      const status = isOk ? 'uptime' : 'downtime';
      
      return {
        id: `step-${paso.id_paso || pasoIdx}`,
        label: stepName,
        color: STATUS_COLORS[status] || '#CBD5E1',
        tooltip: `${stepName} - ${isOk ? 'OK' : errors.map(e => e.nombre || 'Error').join(', ')}`,
        raw: paso,
      };
    });
  }, [selectedNode, okCodesSet]);

  const activeItems = useMemo(() => {
    if (selectedNode) return stepItems;
    if (selectedObjective) return nodeItems;
    return objectiveItems;
  }, [selectedNode, selectedObjective, stepItems, nodeItems, objectiveItems]);

  const hexLayout = useMemo(() => computeHexLayout(activeItems.length), [activeItems.length]);

  const handleSelectObjective = useCallback((item) => {
    setAnimPhase('exiting');
    setTimeout(() => {
      setSelectedObjective(item.raw);
      setSelectedNode(null);
      setAnimPhase('entering');
    }, 300);
  }, []);

  const handleSelectNode = useCallback((item) => {
    setAnimPhase('exiting');
    setTimeout(() => {
      setSelectedNode(item.raw);
      setAnimPhase('entering');
    }, 300);
  }, []);

  const handleBack = useCallback(() => {
    setAnimPhase('exiting');
    setTimeout(() => {
      if (selectedNode) {
        setSelectedNode(null);
      } else {
        setSelectedObjective(null);
      }
      setAnimPhase('entering');
    }, 300);
  }, [selectedNode]);

  const handleStepClick = useCallback((item) => {
    if (!selectedObjective) return;
    history.push({
      pathname: `/online/${selectedObjective.objetivo_id}/${selectedObjective.nombre}/${selectedObjective.nombre_servicio_unificado || selectedObjective.nombre}`,
      state: { fromObjective: selectedObjective },
    });
  }, [selectedObjective, history]);

  React.useEffect(() => {
    if (animPhase === 'entering') {
      const timer = setTimeout(() => setAnimPhase('idle'), 350);
      return () => clearTimeout(timer);
    }
  }, [animPhase]);

  const wrapperClass = `${classes.gridWrapper} ${
    animPhase === 'exiting' ? classes.exiting : classes.entering
  }`;

  const breadcrumbText = useMemo(() => {
    if (!selectedObjective) return '';
    if (selectedNode) {
      return `${selectedObjective.nombre} > ${selectedNode.titulo || selectedNode.nombre}`;
    }
    return selectedObjective.nombre;
  }, [selectedObjective, selectedNode]);

  return (
    <Box className={classes.content}>
      {selectedObjective && (
        <>
          <IconButton size="small" className={classes.backButton} onClick={handleBack}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography className={classes.breadcrumb}>
            {breadcrumbText}
          </Typography>
        </>
      )}

      {loading  ? (
        <Box className={classes.emptyState}>
          <AtCircularProgress />
        </Box>
      ) : activeItems.length === 0 && !isLoadingNodes ? (
        <Box className={classes.emptyState}>
          <span className={classes.emptyIcon}>⬡</span>
          <Typography className={classes.emptyText}>
            {t('home.systemHealth.noObjectives')}
          </Typography>
        </Box>
      ) : isLoadingNodes && selectedObjective ? (
        <Box className={classes.emptyState}>
          <AtCircularProgress />
        </Box>
      ) : (
        <div className={wrapperClass}>
          <HexGridView
            items={activeItems}
            columns={hexLayout.columns}
            layoutSize={hexLayout.layoutSize}
            viewBox={hexLayout.viewBox}
            onClickItem={
              selectedNode
                ? handleStepClick
                : selectedObjective
                ? handleSelectNode
                : handleSelectObjective
            }
            onHoverItem={handleHover}
            onLeaveItem={handleLeave}
            classes={classes}
          />
        </div>
      )}
      
      {/* Custom stylized tooltip */}
      {tooltipInfo.visible && (
        <div style={{
          position: 'fixed',
          top: tooltipInfo.y + 15,
          left: tooltipInfo.x + 15,
          pointerEvents: 'none',
          zIndex: 9999,
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          color: '#fff',
          padding: '8px 12px',
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: 600,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
          whiteSpace: 'nowrap',
          border: '1px solid rgba(255,255,255,0.1)',
          backdropFilter: 'blur(4px)',
          transition: 'opacity 0.1s ease-in',
        }}>
          {tooltipInfo.text}
        </div>
      )}
    </Box>
  );
};

export const HoneycombPanel = withRouter(HoneycombPanelBase);